import React, { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CloudUpload,
  Globe2,
  LayoutDashboard,
  Loader2,
  Lock,
  LogOut,
  Menu,
  Radio,
  Server,
  Shield,
  ShieldCheck,
  User,
  Users,
  X,
} from "lucide-react";
import MasterDashboard from "./pages/MasterDashboard";
import FirmwarePage from "./pages/FirmwarePage";
import { apiUrl } from "./api";
import { ROLES, normalizeRole } from "./roles";
import MfaSecuritySection from "./components/MfaSecuritySection";
import MfaVerification from "./components/MfaVerification";

const NAV_ITEMS = [
  { id: "overview", label: "Overview", labelHe: "סקירה", icon: LayoutDashboard },
  { id: "customers", label: "Customers", labelHe: "לקוחות", icon: Users },
  { id: "sites", label: "Sites", labelHe: "אתרים", icon: Radio },
  { id: "devices", label: "Devices / Systems", labelHe: "מכשירים / מערכות", icon: Server },
  { id: "firmware", label: "Firmware / Technical", labelHe: "קושחה / טכני", icon: CloudUpload },
  { id: "security", label: "Security", labelHe: "אבטחה", icon: Shield },
];

const TEXT = {
  en: {
    brand: "YANAYTECH",
    product: "Platform Portal",
    title: "Yanaytech Platform Portal",
    subtitle: "Super Admin management for TagWeigh customers, sites, devices and firmware.",
    username: "Username",
    password: "Password",
    signIn: "Sign in",
    signingIn: "Signing in",
    required: "Enter username and password.",
    loginFailed: "Login failed.",
    networkError: "Network error.",
    accessDenied:
      "This portal is reserved for Yanaytech Super Admin accounts. Use the Customer Dashboard for customer operations.",
    logout: "Logout",
    status: "Platform access",
    roleGate: "Super Admin only",
    language: "Language",
    menu: "Menu",
    closeMenu: "Close menu",
    mfaTitle: "Account Security",
    mfaSubtitle: "Manage your own two-factor authentication for this Super Admin account.",
  },
  he: {
    brand: "YANAYTECH",
    product: "פורטל פלטפורמה",
    title: "פורטל ניהול Yanaytech",
    subtitle: "ניהול Super Admin ללקוחות, אתרים, מכשירים וקושחה של TagWeigh.",
    username: "שם משתמש",
    password: "סיסמה",
    signIn: "התחברות",
    signingIn: "מתחבר",
    required: "נא להזין שם משתמש וסיסמה.",
    loginFailed: "ההתחברות נכשלה.",
    networkError: "שגיאת תקשורת.",
    accessDenied:
      "הפורטל מיועד לחשבונות Yanaytech Super Admin בלבד. יש להשתמש בדשבורד הלקוחות לפעילות לקוח.",
    logout: "התנתק",
    status: "גישה לפלטפורמה",
    roleGate: "Super Admin בלבד",
    language: "שפה",
    menu: "תפריט",
    closeMenu: "סגור תפריט",
    mfaTitle: "אבטחת חשבון",
    mfaSubtitle: "ניהול האימות הדו-שלבי לחשבון ה-Super Admin שלך.",
  },
};

const normalizeLanguage = (value) => (value === "en" ? "en" : "he");

const readLanguage = () => {
  if (typeof window === "undefined") return "he";
  return normalizeLanguage(window.localStorage.getItem("tagweighPlatformLanguage"));
};

const authKeys = ["userToken", "userData", "role", "id"];

const clearAuth = () => {
  authKeys.forEach((key) => localStorage.removeItem(key));
};

const Login = ({ language, onLanguageChange, onLoginSuccess }) => {
  const t = TEXT[language] || TEXT.he;
  const isRtl = language === "he";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaChallenge, setMfaChallenge] = useState(null);

  const completeLogin = (data) => {
    const normalizedRole = normalizeRole(data?.user?.role);
    if (normalizedRole !== ROLES.SUPER_ADMIN) {
      clearAuth();
      setMfaChallenge(null);
      setError(t.accessDenied);
      return;
    }

    localStorage.setItem("userToken", data.token);
    localStorage.setItem("userData", JSON.stringify(data.user));
    localStorage.setItem("role", normalizedRole);
    localStorage.setItem("id", data.user.id?.toString() || "");
    setMfaChallenge(null);
    onLoginSuccess();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username || !password) {
      setError(t.required);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(apiUrl("/auth?action=login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      const normalizedRole = normalizeRole(data?.user?.role);

      if (response.ok && data?.mfa_required && data?.challenge_token) {
        if (normalizedRole !== ROLES.SUPER_ADMIN) {
          clearAuth();
          setError(t.accessDenied);
          return;
        }
        setPassword("");
        setMfaChallenge({
          token: data.challenge_token,
          user: data.user || { username },
          passkeyAvailable: Boolean(data.passkey_available),
        });
        return;
      }

      if (!response.ok || !data?.token) {
        setError(data?.message || t.loginFailed);
        return;
      }
      completeLogin(data);
    } catch (error) {
      setError(t.networkError);
    } finally {
      setLoading(false);
    }
  };

  if (mfaChallenge) {
    return (
      <div
        className={[
          "min-h-screen bg-[#0B1020] text-white",
          language === "he" ? "language-he" : "language-en",
        ].join(" ")}
        dir={isRtl ? "rtl" : "ltr"}
        lang={language}
      >
        <div className="flex min-h-screen items-center justify-center px-5 py-10">
          <div className="w-full max-w-[460px] rounded-[2rem] border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur">
            <MfaVerification
              challengeToken={mfaChallenge.token}
              passkeyAvailable={mfaChallenge.passkeyAvailable}
              language={language}
              onVerified={completeLogin}
              onCancel={() => {
                setMfaChallenge(null);
                setError("");
                clearAuth();
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        "min-h-screen bg-[#0B1020] text-white",
        language === "he" ? "language-he" : "language-en",
      ].join(" ")}
      dir={isRtl ? "rtl" : "ltr"}
      lang={language}
    >
      <div className="flex min-h-screen items-center justify-center px-5 py-10">
        <div className="w-full max-w-[460px]">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 grid h-20 w-20 place-items-center rounded-3xl border border-white/10 bg-white/10 shadow-2xl">
              <ShieldCheck size={36} className="text-[#8EEBFF]" />
            </div>
            <p className="text-xs font-black uppercase tracking-[0.28em] text-[#8EEBFF]">
              {t.brand}
            </p>
            <h1 className="mt-3 text-3xl font-black">{t.title}</h1>
            <p className="mt-3 text-sm font-bold leading-6 text-white/55">
              {t.subtitle}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-[2rem] border border-white/10 bg-white/[0.06] p-7 shadow-2xl backdrop-blur"
          >
            <div className="mb-5 flex justify-center">
              <label className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-black">
                <Globe2 size={16} />
                <select
                  value={language}
                  onChange={(event) => onLanguageChange(event.target.value)}
                  className="bg-transparent text-white outline-none"
                  aria-label={t.language}
                >
                  <option className="text-slate-900" value="he">HE</option>
                  <option className="text-slate-900" value="en">EN</option>
                </select>
              </label>
            </div>

            {error && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-300/20 bg-red-500/10 px-4 py-3 text-sm font-bold text-red-100">
                <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <label className="mb-4 block">
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-white/45">
                {t.username}
              </span>
              <span className="relative block">
                <User
                  className="absolute top-1/2 -translate-y-1/2 text-white/35"
                  style={isRtl ? { right: 16 } : { left: 16 }}
                  size={19}
                />
                <input
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  className={[
                    "w-full rounded-2xl border border-white/10 bg-[#111827] py-4 text-white outline-none transition focus:border-[#8EEBFF]",
                    isRtl ? "pr-12 pl-4" : "pl-12 pr-4",
                  ].join(" ")}
                  autoComplete="username"
                />
              </span>
            </label>

            <label className="mb-6 block">
              <span className="mb-2 block text-xs font-black uppercase tracking-[0.18em] text-white/45">
                {t.password}
              </span>
              <span className="relative block">
                <Lock
                  className="absolute top-1/2 -translate-y-1/2 text-white/35"
                  style={isRtl ? { right: 16 } : { left: 16 }}
                  size={19}
                />
                <input
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={[
                    "w-full rounded-2xl border border-white/10 bg-[#111827] py-4 text-white outline-none transition focus:border-[#8EEBFF]",
                    isRtl ? "pr-12 pl-4" : "pl-12 pr-4",
                  ].join(" ")}
                  type="password"
                  autoComplete="current-password"
                />
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#8EEBFF] px-5 py-4 text-sm font-black uppercase tracking-[0.18em] text-[#07144C] shadow-xl shadow-cyan-500/15 transition hover:bg-white disabled:opacity-60"
            >
              {loading && <Loader2 size={18} className="animate-spin" />}
              {loading ? t.signingIn : t.signIn}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const Portal = ({ language, setLanguage, onLogout }) => {
  const [view, setView] = useState("overview");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const t = TEXT[language] || TEXT.he;
  const isRtl = language === "he";

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);

  const activePage =
    view === "firmware" ? (
      <FirmwarePage language={language} />
    ) : view === "security" ? (
      <SecurityPage language={language} />
    ) : (
      <MasterDashboard language={language} initialView={view} />
    );

  return (
    <div
      className={[
        "min-h-screen bg-[#F2F7FF] text-[#101633]",
        language === "he" ? "language-he" : "language-en",
      ].join(" ")}
      dir={isRtl ? "rtl" : "ltr"}
      lang={language}
    >
      <aside
        className={[
          "fixed inset-y-0 hidden w-[280px] flex-col bg-[linear-gradient(180deg,#07144C_0%,#102DCE_100%)] text-white shadow-2xl lg:flex",
          isRtl ? "right-0" : "left-0",
        ].join(" ")}
      >
        <div className="border-b border-white/10 px-5 py-6">
          <div className="flex items-center gap-4">
            <img
              src="/logo.jpg"
              alt="TagWeigh"
              className="h-12 w-12 rounded-2xl object-cover ring-1 ring-white/20"
            />
            <div>
              <p className="text-xl font-black tracking-normal">{t.brand}</p>
              <p className="mt-1 text-[10px] font-black uppercase tracking-[0.24em] text-[#8EEBFF]">
                {t.product}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5">
          <div className="space-y-1.5">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = item.id === view;
              return (
                <button
                  key={item.id}
                  onClick={() => setView(item.id)}
                  className={[
                    "flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-start transition",
                    active
                      ? "bg-white text-[#101633] shadow-lg"
                      : "text-white/70 hover:bg-white/10 hover:text-white",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "grid h-9 w-9 place-items-center rounded-xl",
                      active ? "bg-[#EAF8FF] text-[#1C7CFF]" : "bg-white/10",
                    ].join(" ")}
                  >
                    <Icon size={20} />
                  </span>
                  <span className="text-sm font-black">
                    {language === "he" ? item.labelHe : item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <div className="border-t border-white/10 p-3">
          <div className="mb-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
            <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-white/40">
              <Activity size={14} />
              {t.status}
            </div>
            <p className="text-sm font-black text-white">{t.roleGate}</p>
          </div>
          <button
            onClick={onLogout}
            className="flex w-full items-center justify-center gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3.5 font-black text-red-200 transition hover:bg-red-500/20"
          >
            <LogOut size={19} />
            {t.logout}
          </button>
        </div>
      </aside>

      <main className={isRtl ? "lg:mr-[280px]" : "lg:ml-[280px]"}>
        <header className="sticky top-0 z-30 border-b border-[#D7E5FF] bg-white/88 px-4 py-4 backdrop-blur-2xl sm:px-6 lg:px-7">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-[#D7E5FF] bg-white text-[#243158] shadow-sm transition hover:bg-[#F2F7FF] lg:hidden"
                aria-label={t.menu}
                aria-expanded={mobileMenuOpen}
              >
                <Menu size={22} />
              </button>
              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#617096]">
                  {t.product}
                </p>
                <h1 className="mt-1 truncate text-xl font-black sm:text-2xl">{t.title}</h1>
              </div>
            </div>
            <label className="flex items-center gap-2 rounded-xl border border-[#D7E5FF] bg-white px-3 py-2 shadow-sm">
              <Globe2 size={16} className="text-[#617096]" />
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value)}
                className="bg-transparent text-xs font-black uppercase text-[#243158] outline-none"
                aria-label={t.language}
              >
                <option value="he">HE</option>
                <option value="en">EN</option>
              </select>
            </label>
          </div>
        </header>
        <div className="px-4 py-5 sm:px-6 lg:px-7 lg:py-7">{activePage}</div>
      </main>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/55"
            onClick={() => setMobileMenuOpen(false)}
            aria-label={t.closeMenu}
          />
          <div
            className={[
              "absolute inset-y-0 flex w-[min(86vw,320px)] flex-col bg-[linear-gradient(180deg,#07144C_0%,#102DCE_100%)] text-white shadow-2xl",
              isRtl ? "right-0" : "left-0",
            ].join(" ")}
          >
            <div className="border-b border-white/10 px-5 py-5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <img
                    src="/logo.jpg"
                    alt="TagWeigh"
                    className="h-11 w-11 rounded-2xl object-cover ring-1 ring-white/20"
                  />
                  <div>
                    <p className="text-lg font-black tracking-normal">{t.brand}</p>
                    <p className="mt-1 text-[10px] font-black uppercase tracking-[0.2em] text-[#8EEBFF]">
                      {t.product}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="grid h-11 w-11 place-items-center rounded-xl border border-white/10 bg-white/10 text-white"
                  aria-label={t.closeMenu}
                >
                  <X size={21} />
                </button>
              </div>
            </div>

            <nav className="flex-1 overflow-y-auto px-3 py-5">
              <div className="space-y-1.5">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = item.id === view;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setView(item.id);
                        setMobileMenuOpen(false);
                      }}
                      className={[
                        "flex min-h-[48px] w-full items-center gap-3 rounded-2xl px-3 py-3 text-start transition",
                        active
                          ? "bg-white text-[#101633] shadow-lg"
                          : "text-white/75 hover:bg-white/10 hover:text-white",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "grid h-9 w-9 shrink-0 place-items-center rounded-xl",
                          active ? "bg-[#EAF8FF] text-[#1C7CFF]" : "bg-white/10",
                        ].join(" ")}
                      >
                        <Icon size={20} />
                      </span>
                      <span className="text-sm font-black">
                        {language === "he" ? item.labelHe : item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </nav>

            <div className="border-t border-white/10 p-3">
              <div className="mb-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-white/40">
                  <Activity size={14} />
                  {t.status}
                </div>
                <p className="text-sm font-black text-white">{t.roleGate}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  onLogout();
                }}
                className="flex min-h-[48px] w-full items-center justify-center gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3.5 font-black text-red-200 transition hover:bg-red-500/20"
              >
                <LogOut size={19} />
                {t.logout}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SecurityPage = ({ language }) => {
  const t = TEXT[language] || TEXT.he;
  const isRtl = language === "he";

  return (
    <section dir={isRtl ? "rtl" : "ltr"} className="mx-auto max-w-5xl">
      <div className="mb-5 rounded-[2rem] border border-[#D7E5FF] bg-white p-6 shadow-sm">
        <p className="text-[11px] font-black uppercase tracking-[0.22em] text-[#617096]">
          {t.status}
        </p>
        <h2 className="mt-2 text-3xl font-black text-[#101633]">{t.mfaTitle}</h2>
        <p className="mt-2 max-w-2xl text-sm font-bold leading-6 text-[#617096]">
          {t.mfaSubtitle}
        </p>
      </div>
      <div className="rounded-[2rem] border border-[#D7E5FF] bg-white p-6 shadow-sm">
        <MfaSecuritySection language={language} />
      </div>
    </section>
  );
};

const App = () => {
  const [language, setLanguageState] = useState(readLanguage);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem("userToken");
    const role = normalizeRole(localStorage.getItem("role"));
    return Boolean(token) && role === ROLES.SUPER_ADMIN;
  });

  const setLanguage = (value) => {
    const nextLanguage = normalizeLanguage(value);
    localStorage.setItem("tagweighPlatformLanguage", nextLanguage);
    setLanguageState(nextLanguage);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const role = normalizeRole(localStorage.getItem("role"));
    if (role !== ROLES.SUPER_ADMIN) {
      clearAuth();
      setIsAuthenticated(false);
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    clearAuth();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return (
      <Login
        language={language}
        onLanguageChange={setLanguage}
        onLoginSuccess={() => setIsAuthenticated(true)}
      />
    );
  }

  return (
    <Portal
      language={language}
      setLanguage={setLanguage}
      onLogout={handleLogout}
    />
  );
};

export default App;
